import React, { useEffect, useMemo, useState } from 'react';

import {
  clearConsultTraces,
  fetchConsultationPatients,
  fetchJson,
  getResolvedUserNo,
  readConsultTraces,
} from '../utils/agentUiApi';
import './GatewayTraceConsolePage.css';

function formatDateTime(value) {
  if (!value) {
    return '-';
  }
  try {
    return new Date(value).toLocaleString('ko-KR');
  } catch {
    return String(value);
  }
}

function getTracePatientId(trace) {
  const raw = trace?.scope?.patient_id ?? trace?.response?.trace?.forwarded_payload?.patient_id ?? trace?.request?.patient_id;
  return raw === null || raw === undefined ? '' : String(raw);
}

function getTraceReportId(trace) {
  const raw = trace?.scope?.report_id ?? trace?.response?.trace?.forwarded_payload?.report_id ?? trace?.request?.report_id;
  return raw === null || raw === undefined ? '' : String(raw);
}

function sameTraceValue(left, right) {
  if (left === null || left === undefined || right === null || right === undefined) {
    return true;
  }
  return String(left) === String(right);
}

function getTraceScopeIssues(trace) {
  if (!trace) {
    return [];
  }

  const scopePatientId = trace?.scope?.patient_id ?? trace?.request?.patient_id;
  const forwardedPatientId = trace?.response?.trace?.forwarded_payload?.patient_id;
  const responsePatientId = trace?.response?.report_context?.patient_id;
  const memoryHits = Array.isArray(trace?.response?.personal_context?.memory_hits)
    ? trace.response.personal_context.memory_hits
    : [];
  const issues = [];

  if (!sameTraceValue(scopePatientId, forwardedPatientId)) {
    issues.push(`request patient_id ${scopePatientId}와 forwarded patient_id ${forwardedPatientId}가 다릅니다.`);
  }
  if (!sameTraceValue(scopePatientId, responsePatientId)) {
    issues.push(`request patient_id ${scopePatientId}와 report_context patient_id ${responsePatientId}가 다릅니다.`);
  }
  memoryHits.forEach((hit) => {
    if (!sameTraceValue(scopePatientId, hit?.patient_id)) {
      issues.push(`memory hit #${hit?.id || '-'} patient_id ${hit?.patient_id}가 다릅니다.`);
    }
  });

  return issues;
}

function getPatientOptionId(patient) {
  const raw = patient?.std_patient_id ?? patient?.patient_id ?? patient?.id;
  return raw === null || raw === undefined ? '' : String(raw);
}

function formatPatientOptionLabel(patient) {
  const id = getPatientOptionId(patient);
  const rawName = patient?.name || patient?.patient_name || patient?.display_name || patient?.label || '';
  const name = String(rawName || '').trim() || `환자 ${id}`;
  return `가족 ${id} / ${name}`;
}

function GatewayTraceConsolePage() {
  const [traces, setTraces] = useState(() => readConsultTraces());
  const [selectedTraceId, setSelectedTraceId] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [patients, setPatients] = useState([]);
  const [patientsError, setPatientsError] = useState('');
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState('');
  const userNo = getResolvedUserNo();

  const patientOptions = useMemo(() => {
    const options = patients
      .map((patient) => ({
        id: getPatientOptionId(patient),
        label: formatPatientOptionLabel(patient),
      }))
      .filter((patient) => patient.id);

    const knownIds = new Set(options.map((patient) => patient.id));
    traces.forEach((trace) => {
      const tracePatientId = getTracePatientId(trace);
      if (tracePatientId && !knownIds.has(tracePatientId)) {
        knownIds.add(tracePatientId);
        options.push({
          id: tracePatientId,
          label: `가족 ${tracePatientId} / trace 기준 환자`,
        });
      }
    });

    return options;
  }, [patients, traces]);

  const patientLabelById = useMemo(() => {
    const entries = patientOptions.map((patient) => [patient.id, patient.label]);
    return new Map(entries);
  }, [patientOptions]);

  const filteredTraces = useMemo(() => {
    if (!selectedPatientId) {
      return traces;
    }
    return traces.filter((trace) => getTracePatientId(trace) === selectedPatientId);
  }, [selectedPatientId, traces]);

  useEffect(() => {
    if (!filteredTraces.length) {
      if (selectedTraceId) {
        setSelectedTraceId('');
      }
      return;
    }

    if (!filteredTraces.some((trace) => trace.id === selectedTraceId)) {
      setSelectedTraceId(filteredTraces[0].id);
    }
  }, [filteredTraces, selectedTraceId]);

  useEffect(() => {
    let cancelled = false;

    const loadPatients = async () => {
      if (!userNo) {
        setPatients([]);
        return;
      }

      try {
        const patientList = await fetchConsultationPatients(userNo);
        if (cancelled) {
          return;
        }
        const normalizedPatients = Array.isArray(patientList) ? patientList : [];
        const tracePatientId = traces.length ? getTracePatientId(traces[0]) : '';
        const defaultPatient = normalizedPatients.find((patient) => patient?.is_default) || normalizedPatients[0];
        const defaultPatientId = tracePatientId || getPatientOptionId(defaultPatient);

        setPatients(normalizedPatients);
        setPatientsError('');
        setSelectedPatientId((previous) => previous || defaultPatientId || '');
      } catch (loadError) {
        if (!cancelled) {
          setPatients([]);
          setPatientsError(loadError?.message || '환자 목록을 불러오지 못했습니다.');
        }
      }
    };

    loadPatients();

    return () => {
      cancelled = true;
    };
  }, [traces, userNo]);

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        const response = await fetchJson('/api/v1/chatbot/poc/metrics');
        setMetrics(response);
      } catch (metricsError) {
        setError(metricsError.message || 'metrics 조회에 실패했습니다.');
      }
    };
    loadMetrics();
  }, []);

  const selectedTrace = useMemo(
    () => filteredTraces.find((item) => item.id === selectedTraceId) || filteredTraces[0] || null,
    [filteredTraces, selectedTraceId],
  );
  const selectedTraceScopeIssues = useMemo(() => getTraceScopeIssues(selectedTrace), [selectedTrace]);

  const handleRefresh = () => {
    setTraces(readConsultTraces());
    setError('');
  };

  const handleClear = () => {
    clearConsultTraces();
    setTraces([]);
    setSelectedTraceId('');
  };

  const bridgeRows = selectedTrace
    ? [
        {
          label: '가족(patient_id)',
          requestValue: selectedTrace.request?.patient_id,
          forwardedValue: selectedTrace.response?.trace?.forwarded_payload?.patient_id,
        },
        {
          label: '리포트(report_id)',
          requestValue: selectedTrace.request?.report_id || '-',
          forwardedValue: selectedTrace.response?.trace?.forwarded_payload?.report_id || '-',
        },
        {
          label: '세션(session_id)',
          requestValue: selectedTrace.request?.session_id,
          forwardedValue: selectedTrace.response?.trace?.forwarded_payload?.session_id,
        },
      ]
    : [];

  return (
    <div className="gateway-trace-page">
      <section className="gateway-trace-hero">
        <div>
          <p className="gateway-trace-eyebrow">UI-GATEWAY-01</p>
          <h1>Gateway 상담 라우팅 추적 패널</h1>
          <p>
            사용자 입력, gateway 전달 payload, report/session/patient 브리징, guardrail 응답을 하나의 trace로 확인합니다.
          </p>
        </div>
        <div className="gateway-trace-actions">
          <button type="button" onClick={handleRefresh}>trace 새로고침</button>
          <button type="button" onClick={handleClear}>trace 비우기</button>
        </div>
      </section>

      {error ? <div className="gateway-trace-error">{error}</div> : null}

      <section className="gateway-trace-grid">
        <aside className="gateway-trace-sidebar">
          <article className="gateway-trace-card">
            <h2>최근 상담 Trace</h2>
            <label className="gateway-trace-filter">
              가족(patient_id)
              <select value={selectedPatientId} onChange={(event) => setSelectedPatientId(event.target.value)}>
                <option value="">전체 환자 Trace</option>
                {patientOptions.map((patient) => (
                  <option key={patient.id} value={patient.id}>
                    {patient.label}
                  </option>
                ))}
              </select>
              <span>
                {selectedPatientId
                  ? `${patientLabelById.get(selectedPatientId) || `가족 ${selectedPatientId}`} 기준 ${filteredTraces.length}건`
                  : `전체 ${filteredTraces.length}건`}
              </span>
            </label>
            {patientsError ? <p className="gateway-trace-error">{patientsError}</p> : null}
            {filteredTraces.length ? (
              <div className="gateway-trace-list">
                {filteredTraces.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className={`gateway-trace-list-item ${selectedTrace?.id === item.id ? 'active' : ''}`}
                    onClick={() => setSelectedTraceId(item.id)}
                  >
                    <strong>{item.request?.question || '질문 없음'}</strong>
                    <em>
                      {patientLabelById.get(getTracePatientId(item)) || `가족 ${getTracePatientId(item) || '-'}`}
                      {getTraceReportId(item) ? ` · 리포트 ${getTraceReportId(item)}` : ''}
                    </em>
                    <span>{formatDateTime(item.createdAt)}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="gateway-trace-empty">선택한 환자의 상담 trace가 없습니다. 상담 화면에서 해당 가족으로 요청을 실행해 주세요.</p>
            )}
          </article>

          <article className="gateway-trace-card">
            <h2>PoC Metrics</h2>
            <pre>{JSON.stringify(metrics || {}, null, 2)}</pre>
          </article>
        </aside>

        <main className="gateway-trace-main">
          <article className="gateway-trace-card">
            <h2>브리징 확인</h2>
            {selectedTrace ? (
              <>
                <div className={`gateway-trace-scope-banner ${selectedTraceScopeIssues.length ? 'is-warning' : 'is-ok'}`}>
                  <strong>{selectedTraceScopeIssues.length ? '환자 컨텍스트 점검 필요' : '환자 컨텍스트 일치'}</strong>
                  <span>
                    {selectedTraceScopeIssues.length
                      ? selectedTraceScopeIssues[0]
                      : 'request, forwarded payload, report context, memory hit가 같은 patient_id 기준입니다.'}
                  </span>
                </div>
                <table className="gateway-trace-table">
                  <thead>
                    <tr>
                      <th>필드</th>
                      <th>브라우저 요청</th>
                      <th>gateway 전달값</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bridgeRows.map((row) => (
                      <tr key={row.label}>
                        <td>{row.label}</td>
                        <td>{String(row.requestValue ?? '-')}</td>
                        <td>{String(row.forwardedValue ?? '-')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <p className="gateway-trace-empty">선택된 trace가 없습니다.</p>
            )}
          </article>

          <section className="gateway-trace-detail-grid">
            <article className="gateway-trace-card">
              <h2>사용자 입력 원문</h2>
              <pre>{JSON.stringify(selectedTrace?.request || {}, null, 2)}</pre>
            </article>

            <article className="gateway-trace-card">
              <h2>Forwarded Payload</h2>
              <pre>{JSON.stringify(selectedTrace?.response?.trace?.forwarded_payload || {}, null, 2)}</pre>
            </article>

            <article className="gateway-trace-card">
              <h2>최종 응답 핵심 필드</h2>
              <pre>
                {JSON.stringify(
                  selectedTrace
                    ? {
                        intent: selectedTrace.response?.intent,
                        risk_level: selectedTrace.response?.risk_level,
                        followup_action: selectedTrace.response?.followup_action,
                        guardrail: selectedTrace.response?.guardrail,
                        operational_signals: selectedTrace.response?.operational_signals,
                        report_context: selectedTrace.response?.report_context,
                      }
                    : {},
                  null,
                  2,
                )}
              </pre>
            </article>
          </section>
        </main>
      </section>
    </div>
  );
}

export default GatewayTraceConsolePage;
